import {
    Button,
    LoadingDots,
    Message,
    ModalBody,
    ModalDialog,
    ModalFooter,
    ModalHeader,
    ModalOverlay
} from '@flos/react-ui';
import useEditCoverablePrice from '../../../hooks/useEditCoverablePrice';
import AttributeChangeInformation from './EditCoverableAttribute/AttributeChangeInformation';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { opcInfoAtom } from '../../../recoil/atoms/opcInfoAtom';
import getEditAttributeConfig from '../config/EditAttribute';
import { getEditCoverableKey, isPrivateHouse } from '../utils/editAttributeUtil';
import { useHippoText } from '../../../hooks/useHippoText';
import usePageNavigation from '../../../hooks/usePageNavigation';
import { jobAtom } from '../../../recoil/atoms/jobAtom';

import HighlightInfoForCoverage from './HighlightInfoForCoverage';
import type { CustomerStopCause } from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { HandleCloseOPCModal, OpcCoverableAttribute } from '../types';
import { isOpcDataFilledAtom } from '../../../recoil/atoms/isOpcDataFilledAtom';
import { isOpcFetchingAtom } from '../../../recoil/atoms/isOpcFetchingAtom';
import { callMeVariantOpcAtom } from '../../../recoil/atoms/callMeVariantOpcAtom';
import { track } from '../../../utils/trackingUtil';
import useOpcJob from '../../../hooks/useOpcJob';
import { opcCoverableStatusSelector } from '../../../recoil/selectors/opcCoverableStatusSelector';
import { useQuoteJob } from '../../../hooks/useQuoteJob';
import { useAwaitQuoteJob } from '../../../hooks/useAwaitQuotedJob';
import { useIssueInsurance } from '../../../hooks/useIssueInsurance';
import { useAwaitBoundJob } from '../../../hooks/useAwaitBoundJob';
import { useRef, useState } from 'react';
import LoadingModal from '../../../shared_components/LoadingModal/LoadingModal';
import CustomerStopModal from '../../../shared_components/CustomerStopModal/CustomerStopModal';
import { allCoverablesAtom } from '../../../recoil/atoms/allCoverablesAtom';
import { refreshCoverablesSelectorAtom } from '../../../recoil/atoms/refreshCoverablesSelectorAtom';
import { getEffectiveDateRuleInfoSelector } from '../../../recoil/selectors/getEffectiveDateRuleSelector';
import { convertLocalDateDTOToDate, convertLocalDateToString } from '../../../utils/dateUtil';
import { carOPCYearlyMileageStepAtom } from '../../../recoil/atoms/carOPCYearlyMileageStepAtom';

const getContinueBtnHippoKey = (attribute: string): string => {
    switch (attribute) {
        case 'carOdometer':
            return 'DSS-ENTER-ODOMETER-READING-FIELD-CONTINUE';
        case 'dogClub':
        case 'prcVoluntaryAlarm':
        case 'adultAccFulltimeLeisure':
        case 'adultAccEmployment':
        case 'traNumberInHousehold':
            // Todo: Use hippo label when label id is define in document
            return 'Bekræft ændring';
        case 'catChipId':
        case 'dogChipId':
            return 'Gem';
        case 'choInsurancePeriod':
            return 'DSS-COVERABLE-ADD-CHANGE';
        default:
            return 'DSS-COVERABLE-DETAILS-ACCEPT';
    }
};

const getBackBtnHippoKey = (attribute: string): string => {
    switch (attribute) {
        case 'dogClub':
        case 'dogChipId':
        case 'catChipId':
            return 'DSS-COMMON-BACK';
        default:
            return 'DSS-COVERABLE-DETAILS-CANCEL';
    }
};

const EditCoverableAttributeModal = ({
    handleCloseModal
}: {
    handleCloseModal: HandleCloseOPCModal;
}): React.JSX.Element => {
    const isFullScreen = false;
    const getHippoText = useHippoText();
    const [opcInfo, setOpcInfo] = useRecoilState(opcInfoAtom);
    const [isOpcDataFilled, setIsOpcDataFilled] = useRecoilState(isOpcDataFilledAtom);
    const isOpcFetching = useRecoilValue(isOpcFetchingAtom);
    const { isRuleActive, effectiveDate } = useRecoilValue(getEffectiveDateRuleInfoSelector);
    // const editCoverableDTO = generateEditCoverableDTO(opcInfo);
    const { updateCoverableAttribute, editCoverablePrice, revertChange, coverableAttribute } =
        useEditCoverablePrice(getEditCoverableKey(opcInfo.productName));
    const { goBackToDetailsPage, goToShoppingBasket, goToReceiptPage } = usePageNavigation();
    const { createNewPolicyChange, withdrawCurrentOPCShoppingBasketIfNotExisting } = useOpcJob();
    const { selectedPaymentFrequency, renewalDate, isOpcJob } = useRecoilValue(jobAtom);
    const setCallMeVariant = useSetRecoilState(callMeVariantOpcAtom);

    const [carOPCYearlyMileageStep, setCarOPCYearlyMileageStep] = useRecoilState(
        carOPCYearlyMileageStepAtom
    );
    const hasOdometerUpdateStep = opcInfo.attribute === 'carYearlyMileage' && isOpcJob;
    const isCarYearlyMileageChangeFirstStep = carOPCYearlyMileageStep === 'yearlyMileage';

    const {
        title,
        AttributeChangeComponent,
        attributeChangeAditionalProps,
        attributeChangeInformationProps
    } = getEditAttributeConfig(opcInfo, getHippoText, editCoverablePrice, selectedPaymentFrequency);
    const editTravelCoverable = editCoverablePrice?.travel?.[0];
    const travelAttribute = editTravelCoverable?.attribute.concat(
        String(editTravelCoverable?.after?.typeKeyValue)
    );
    const isTravelDowngrade =
        editTravelCoverable?.after?.typeKeyDisplayName === 'Rejseforsikring til enkeltperson' &&
        editTravelCoverable?.before?.typeKeyDisplayName === 'Rejseforsikring';
    const continueBtnHippoKey = getContinueBtnHippoKey(opcInfo.attribute ?? '');
    const backBtnHippoKey = getBackBtnHippoKey(opcInfo.attribute ?? '');
    const { isAddedCoverable } = useRecoilValue(opcCoverableStatusSelector);
    const { quoteJob } = useQuoteJob();
    const { awaitQuoteJob } = useAwaitQuoteJob();
    const { issueInsurance } = useIssueInsurance();
    const { awaitBoundJob } = useAwaitBoundJob(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [stopCause, setStopCause] = useState<CustomerStopCause>('None');
    const setJobAtom = useSetRecoilState(jobAtom);
    const [isOpen, setIsOpen] = useState(true);
    const hasTriggeredIssuing = useRef(false);
    const resetCoverables = useResetRecoilState(allCoverablesAtom);
    const setRefreshCoverablesSelector = useSetRecoilState(refreshCoverablesSelectorAtom);

    const hasNoChanges =
        attributeChangeInformationProps?.mobileProps.before.attribute.value !== undefined &&
        attributeChangeInformationProps?.mobileProps.after.attribute.value !== undefined &&
        attributeChangeInformationProps?.mobileProps.before.attribute.value ===
            attributeChangeInformationProps?.mobileProps.after.attribute.value &&
        attributeChangeInformationProps?.mobileProps.before.price.value ===
            attributeChangeInformationProps?.mobileProps.after.price.value;

    const isShowingOldPriceOnly =
        attributeChangeInformationProps?.mobileProps.before.attribute.value !== undefined &&
        attributeChangeInformationProps?.mobileProps.before.price.value !== undefined &&
        attributeChangeInformationProps?.mobileProps.after.attribute.value === '-' &&
        attributeChangeInformationProps?.mobileProps.after.price.value === '-';

    const isAccEmploymentPriceIncrease =
        opcInfo.attribute === 'adultAccEmployment' &&
        Number(editCoverablePrice?.newPrice?.yearlyTotal.amount) >
            Number(editCoverablePrice?.oldPrice?.yearlyTotal.amount);

    const handleBackToDAShoppingBasket = async (): Promise<void> => {
        await revertChange();
        goToShoppingBasket(false);
    };

    const handleBackToDetailsPage = async (): Promise<void> => {
        await withdrawCurrentOPCShoppingBasketIfNotExisting();
        goBackToDetailsPage();
    };

    const handleBackToAttributePage = async (): Promise<void> => {
        await withdrawCurrentOPCShoppingBasketIfNotExisting();
        await createNewPolicyChange();
        setOpcInfo((prev) => {
            return { ...prev, attribute: '' };
        });
    };

    const handleCloseModalOption = async (): Promise<void> => {
        if (opcInfo.attribute === 'carYearlyMileage') {
            setCarOPCYearlyMileageStep('yearlyMileage');
        }
        if (opcInfo.isFromDAFlow) {
            await handleBackToDAShoppingBasket();
        } else if (isPrivateHouse(opcInfo.productName)) {
            await handleBackToAttributePage();
        } else {
            await handleBackToDetailsPage();
        }
    };

    const quoteAndIssueJobInBackground = async (): Promise<void> => {
        if (hasTriggeredIssuing.current) return;
        hasTriggeredIssuing.current = true;
        const response = await quoteJob();
        if (response?.stopCause !== 'None') {
            setStopCause(response?.stopCause);
            setIsOpen(false);
            return;
        } else {
            await awaitQuoteJob();
        }

        await issueInsurance();
        const { status } = await awaitBoundJob();
        setJobAtom((jobDTO) => ({ ...jobDTO, jobStatus: status }));
    };

    const onFinished = (): void => {
        setShowLoadingModal(false);
        if (stopCause === 'None') {
            handleCloseModal(goToReceiptPage, 'goToReceiptPage');
            resetCoverables();
            setRefreshCoverablesSelector({});
        }
    };

    const trackChangedAttributes = (): void => {
        const coverableKey = getEditCoverableKey(opcInfo.productName);
        const firstDiff = editCoverablePrice?.[coverableKey]?.[0];

        if (!firstDiff || !coverableAttribute) return;

        const attribut = firstDiff.attribute;
        const displayName = firstDiff.after?.typeKeyDisplayName ?? firstDiff.after?.stringValue;
        const attributValue = firstDiff.after?.typeKeyValue;
        const remainingInputs: OpcCoverableAttribute[] = [];

        Object.entries(coverableAttribute).forEach(([key, value]) => {
            if (value !== attributValue) {
                remainingInputs.push({ [key]: value });
            }
        });

        track(`${coverableKey}EditAttribute`, [
            {
                newYearlyPrice: editCoverablePrice?.newPrice?.yearlyTotal.amount
            },
            {
                newMonthlyPrice: editCoverablePrice?.newPrice?.monthlyTotal.amount
            },
            { [attribut]: displayName },
            ...remainingInputs
        ]);
    };

    return (
        <ModalOverlay isOpen={isOpen} isFullScreen={isFullScreen}>
            <ModalDialog
                isFullScreen={isFullScreen}
                closeIconHandler={() => {
                    if (isOpcFetching) return;
                    handleCloseModal(handleCloseModalOption);
                }}
            >
                <ModalHeader title={getHippoText(title)} />
                <ModalBody>
                    <AttributeChangeComponent
                        updateCoverableAttribute={updateCoverableAttribute}
                        additionalProps={attributeChangeAditionalProps}
                    />
                    {isOpcFetching ? (
                        <LoadingDots centered />
                    ) : attributeChangeInformationProps ? (
                        <>
                            <AttributeChangeInformation
                                attributeChangeProps={attributeChangeInformationProps}
                            />
                            <HighlightInfoForCoverage
                                coverageId={travelAttribute ?? ''}
                                editCoverageType="add"
                                renewalDate={renewalDate}
                            />
                        </>
                    ) : (
                        <></>
                    )}
                    {isAccEmploymentPriceIncrease && (
                        <Message
                            title={getHippoText(
                                'DSS-COVERABLE-DETAILS-STEP-EMPLOYMENT-PRICE-INCREASE'
                            )}
                            theme="warning"
                        />
                    )}
                    {isRuleActive && !(opcInfo.isFromDAFlow && isAddedCoverable) && (
                        <h6>
                            {getHippoText('DSS-EFFECTIVE-DATE-RULE-MOTOR-TEXT', {
                                date:
                                    convertLocalDateToString(
                                        convertLocalDateDTOToDate(effectiveDate ?? {})
                                    ) ?? ''
                            })}
                        </h6>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        disabled={
                            !isOpcDataFilled ||
                            isOpcFetching ||
                            hasNoChanges ||
                            isShowingOldPriceOnly
                        }
                        onClick={() => {
                            if (hasOdometerUpdateStep && isCarYearlyMileageChangeFirstStep) {
                                setIsOpcDataFilled(false);
                                setCarOPCYearlyMileageStep('odometerReading');
                                return;
                            }
                            if (hasOdometerUpdateStep && !isCarYearlyMileageChangeFirstStep) {
                                setCarOPCYearlyMileageStep('yearlyMileage');
                            }
                            if (isTravelDowngrade) {
                                setCallMeVariant('traNumberInHousehold');
                                return;
                            }
                            trackChangedAttributes();
                            if (opcInfo.isFromDAFlow) {
                                handleCloseModal(goToShoppingBasket, 'goToShoppingBasket');
                            } else {
                                setIsOpen(false);
                                setShowLoadingModal(true);
                            }
                        }}
                    >
                        {hasOdometerUpdateStep &&
                            (isCarYearlyMileageChangeFirstStep
                                ? getHippoText('DSS-ENTER-ODOMETER-READING-FIELD-CONTINUE')
                                : getHippoText('DSS-COVERABLE-DETAILS-ACCEPT'))}
                        {!hasOdometerUpdateStep && getHippoText(continueBtnHippoKey)}
                    </Button>
                    <Button
                        disabled={isOpcFetching}
                        theme="secondary"
                        onClick={() => {
                            handleCloseModal(handleCloseModalOption);
                        }}
                    >
                        {getHippoText(backBtnHippoKey)}
                    </Button>
                </ModalFooter>
            </ModalDialog>
            {showLoadingModal && (
                <LoadingModal
                    loadingTitle={getHippoText('DSS-LOADING-HEADER')}
                    loadingText={getHippoText('DSS-LOADING-TEXT')}
                    showModal={showLoadingModal}
                    closeModal={() => {
                        setShowLoadingModal(false);
                    }}
                    backgroundTask={quoteAndIssueJobInBackground}
                    onFinished={onFinished}
                />
            )}
            {stopCause !== 'None' && (
                <CustomerStopModal
                    showModal={true}
                    stopCause={stopCause}
                    closeModal={() => {
                        setIsOpen(true);
                        setStopCause('None');
                    }}
                />
            )}
        </ModalOverlay>
    );
};

export default EditCoverableAttributeModal;
